import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Box, IconButton, Tooltip as MuiTooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useData } from '../context/DataContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const FillRateBar = ({ selectedIndustries = [], onBarClick = () => {} }) => {
  const [infoOpen, setInfoOpen] = useState(false);
  const { getFillRateSales, dataReady } = useData();

  // Get data from Excel (3 years)
  const excelData = dataReady ? getFillRateSales() : { labels: [], data2022: [], data2023: [], data2024: [] };

  // Use Excel data or fallback
  const allLabels = excelData.labels.length > 0
    ? excelData.labels
    : ['Sector salud/ Farmacéuticas', 'Manufactura', 'Servicios Logísticos', 'Alimentos', 'Retail', 'Automotriz', 'Construcción'];

  const allData2022 = excelData.data2022.length > 0 ? excelData.data2022 : [96, 94, 97, 96, 95, 90, 94];
  const allData2023 = excelData.data2023.length > 0 ? excelData.data2023 : [100, 98, 98, 97, 93, 97, 90];
  const allData2024 = excelData.data2024.length > 0 ? excelData.data2024 : [92, 98, 98, 98, 96, 97, 93];

  const labels = selectedIndustries.length > 0 ? selectedIndustries : allLabels;

  // Map selected labels to their data values
  const getValuesForLabels = (allValues) => labels.map((label) => {
    const index = allLabels.indexOf(label);
    return index !== -1 ? allValues[index] : 0;
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: '2023',
        data: getValuesForLabels(allData2022),
        backgroundColor: '#A2AEBB',
        borderColor: '#fff',
        borderWidth: 1,
      },
      {
        label: '2024',
        data: getValuesForLabels(allData2023),
        backgroundColor: '#476A8E',
        borderColor: '#fff',
        borderWidth: 1,
      },
      {
        label: '2025',
        data: getValuesForLabels(allData2024),
        backgroundColor: '#1F3D58',
        borderColor: '#fff',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 40,
        bottom: 40,
        left: 20,
        right: 20,
      },
    },
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.raw}%`,
        },
      },
      datalabels: {
        color: '#000',
        anchor: 'end',
        align: 'top',
        offset: (context) => {
          // Stagger labels vertically to avoid overlap
          return context.datasetIndex === 0 ? 2 : context.datasetIndex === 1 ? 12 : 22;
        },
        formatter: (value) => `${value}%`,
        font: { weight: 'bold', size: 9 },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Industria',
        },
        stacked: false,
      },
      y: {
        title: {
          display: true,
          text: 'Porcentaje (%)',
        },
        beginAtZero: true,
        max: 115,
        stacked: false,
      },
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const industry = chartData.labels[index];
        onBarClick([industry]);
      }
    },
  };

  const infoContent = {
    formula: 'Fill rate de entrega al cliente (%) = (Total de piezas entregadas / Total de piezas solicitadas) × 100',
    description: 'Mide la cantidad de piezas surtidas vs piezas requeridas por el cliente, en porcentaje.',
  };

  return (
    <div style={{ maxWidth: '800px', height: '500px', margin: '0 auto', padding: '20px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={-5}>
        <MuiTooltip title="Información de la gráfica">
          <IconButton onClick={() => setInfoOpen(true)}>
            <InfoIcon />
          </IconButton>
        </MuiTooltip>
      </Box>
      <div style={{ width: '100%', height: '550px' }}>
        <Bar data={chartData} options={options} />
      </div>
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Información de la Gráfica</DialogTitle>
        <DialogContent>
          <p><strong>Fórmula:</strong> {infoContent.formula}</p>
          <p><strong>Descripción:</strong> {infoContent.description}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FillRateBar;
